import {
  OktonSearchSelect,
  OktonPageHeader,
  OktonInput,
  OktonAlert,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  HStack,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import { useContasBancarias } from '../../../services/hooks/useContasBancarias';
import { useCreateLancamentoExtrato } from '../../../services/hooks/useExtratoBancarioMutations';
import { limparEntradaValor, valorParaNumero } from '../../../utils/valorInput';

// O extrato não tem coluna de tipo: o sinal do valor é que distingue crédito de
// débito. "tipo" existe só no formulário, para montar esse sinal.
const VAZIO = {
  conta: '',
  data: new Date().toISOString().split('T')[0],
  // Opcional: sem hora, o lançamento fica com 00:00, como os que o banco
  // manda sem hora.
  hora: '',
  descricao: '',
  valor: '',
  referencia: '',
  // O extrato do banco é sinalizado por natureza: crédito positivo, débito
  // negativo. Diferente do financeiro, onde o sentido vive em tipo_fin.
  tipo: 'credito' as 'credito' | 'debito',
};

/**
 * Cadastro de um lançamento do extrato, em rota própria como as demais telas
 * de cadastro do OktonBank.
 *
 * Ficar numa rota, e não num pop-up sobre a lista, tem um efeito colateral
 * bom: a lista deixa de carregar o que só o formulário usa.
 */
const NovoLancamentoExtrato = () => {
  const history = useHistory();
  // A lista manda a Conta que estava filtrada: quem clicou em Novo estava
  // olhando para ela, e redigitar seria trabalho repetido.
  const { state } = useLocation<{ conta?: string } | undefined>();
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const { data: accounts = [] } = useContasBancarias();
  const createMutation = useCreateLancamentoExtrato();

  const [form, setForm] = useState({
    ...VAZIO,
    conta: state?.conta ?? '',
  });
  const [erro, setErro] = useState('');
  const [saving, setSaving] = useState(false);

  const salvar = async () => {
    if (!form.conta || !form.descricao.trim() || !form.data) {
      setErro('Conta, data e descrição são obrigatórios.');
      return;
    }
    const valorNumerico = valorParaNumero(form.valor);
    if (!valorNumerico) {
      setErro('Informe um valor maior que zero.');
      return;
    }

    setSaving(true);
    setErro('');

    const valor =
      form.tipo === 'debito'
        ? -Math.abs(valorNumerico)
        : Math.abs(valorNumerico);

    try {
      await createMutation.mutateAsync({
        conta: form.conta,
        data: form.data,
        hora: form.hora,
        descricao: form.descricao.trim(),
        valor: String(valor),
        referencia: form.referencia?.trim() || '',
      });
      history.push('/extrato-bancario');
    } catch (err) {
      setErro(
        err instanceof Error
          ? err.message
          : 'Erro ao salvar lançamento de extrato.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Novo Lançamento - Extrato"
        subtitle="Lançamento importado do banco, registrado à mão"
        onBack={() => history.push('/extrato-bancario')}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        <Stack spacing="4" maxW="720px">
          {!!erro && <OktonAlert status="error" description={erro} />}

          <Box>
            <Text
              as="label"
              htmlFor="conta"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Conta *
            </Text>
            <OktonSearchSelect
              name="conta"
              placeholder="Selecione a conta"
              value={form.conta}
              onChange={valor => setForm(f => ({ ...f, conta: valor }))}
              options={[
                ...accounts.map(a => ({
                  value: a.id_cts,
                  label: a.descricao_cts,
                })),
              ]}
            />
          </Box>

          <SimpleGrid columns={[1, 3]} spacing="4">
            <Box>
              <Text
                as="label"
                htmlFor="extrato-data"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Data *
              </Text>
              <OktonInput
                name="extrato-data"
                type="date"
                value={form.data}
                color={color}
                onChange={e => {
                  const valor = e.target.value;
                  setForm(f => ({ ...f, data: valor }));
                }}
              />
            </Box>
            <Box>
              <Text
                as="label"
                htmlFor="extrato-hora"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Hora
              </Text>
              <OktonInput
                name="extrato-hora"
                type="time"
                value={form.hora}
                color={color}
                onChange={e => {
                  const valor = e.target.value;
                  setForm(f => ({ ...f, hora: valor }));
                }}
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb="1">
                Tipo *
              </Text>
              <HStack overflow="hidden" spacing={2} p="1">
                <Button
                  flex="1"
                  borderRadius="none"
                  colorScheme="green"
                  variant={form.tipo === 'credito' ? 'solid' : 'outline'}
                  onClick={() => setForm(f => ({ ...f, tipo: 'credito' }))}
                >
                  Crédito
                </Button>
                <Button
                  flex="1"
                  borderRadius="none"
                  colorScheme="red"
                  variant={form.tipo === 'debito' ? 'solid' : 'outline'}
                  onClick={() => setForm(f => ({ ...f, tipo: 'debito' }))}
                >
                  Débito
                </Button>
              </HStack>
            </Box>
          </SimpleGrid>

          <Box>
            <Text
              as="label"
              htmlFor="extrato-descricao"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Descrição *
            </Text>
            <OktonInput
              name="extrato-descricao"
              color={color}
              placeholder="Histórico do lançamento"
              maxLength={255}
              value={form.descricao}
              onChange={e => {
                const valor = e.target.value;
                setForm(f => ({ ...f, descricao: valor }));
              }}
            />
          </Box>

          <SimpleGrid columns={[1, 2]} spacing="4">
            <Box>
              <Text
                as="label"
                htmlFor="extrato-valor"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Valor (R$) *
              </Text>
              <OktonInput
                name="extrato-valor"
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                color={color}
                value={form.valor}
                onChange={e => {
                  const valor = e.target.value;
                  setForm(f => ({
                    ...f,
                    valor: limparEntradaValor(valor),
                  }));
                }}
              />
            </Box>
            <Box>
              <Text
                as="label"
                htmlFor="extrato-referencia"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Referência
              </Text>
              <OktonInput
                name="extrato-referencia"
                color={color}
                placeholder="Nº doc / NF"
                maxLength={120}
                value={form.referencia}
                onChange={e => {
                  const valor = e.target.value;
                  setForm(f => ({ ...f, referencia: valor }));
                }}
              />
            </Box>
          </SimpleGrid>

          <Flex gridGap="3" pt="2">
            <Button
              variant="ghost"
              onClick={() => history.push('/extrato-bancario')}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={saving ? <Spinner size="sm" /> : <Check size={16} />}
              isDisabled={saving}
              onClick={salvar}
            >
              Adicionar
            </Button>
          </Flex>
        </Stack>
      </Box>
    </BankAppPage>
  );
};

export default NovoLancamentoExtrato;
